import { IS_ZUMPER_RENTALS } from '@zcode/nr-utils'
import type { ZumperRoute } from '@zcode/router-utils'
import { getLegacyReactRouterParams } from '@zcode/router-utils'
import type { CacheControl } from './cacheControl'
import {
  ABOUT_LANDING,
  ADDR_BASE_URL,
  AFFORDABILITY_CALCULATOR,
  AI_ASSISTANT,
  APTS_BASE_URL,
  BLDG_BASE_URL,
  CAREERS_PAGE,
  CCPA_CONFIRMATION,
  CCPA_REQUEST,
  COMMUNITY_LISTINGS_TERMS,
  CURRENT_SEARCH_ROUTE,
  FAIR_HOUSING,
  FAVORITES_ROUTE,
  FILTERS_PATH_RE,
  FOR_YOU,
  FULL_DETAIL_VIEW,
  GOOGLE_POSTING_SERVICES_TERMS,
  HOMEPAGE,
  HOMEPAGE_ZUMPER_RENTALS,
  LISTING_BASE_URL,
  LISTING_ID_RE,
  MESSAGED_ROUTE,
  MESSAGES_PAGE,
  MESSAGING_BASE_PATH,
  NEAR_ME,
  NEWSROOM_PAGE,
  NOTIFICATION_SETTINGS,
  NURTURE_BOSS_TERMS,
  PERMANENT_ADDRESS,
  PRIVACY_POLICY,
  PRIVACY_POLICY_10_2022,
  RENT_RESEARCH_DETAIL,
  RENT_RESEARCH_HOMEPAGE,
  REVIEWS_TERMS,
  SAVED_SEARCHES_ROUTE,
  SAVED_SEARCHES_SUMMARY_ROUTE,
  SIGNATURE_VIRTUAL_TERMS,
  SIGN_IN,
  SIGN_UP,
  SITEMAP_CHILD_PAGE,
  SITEMAP_ENTRY_POINT,
  SNAPPR_TERMS,
  STR_LANDING,
  STR_PROVIDER_INTERSTITIAL,
  TERMS_AND_CONDITIONS,
  TRANSUNION_TOS,
  VALID_NEAR_ME_TYPE_ROUTES,
  VIRTUAL_TOURS_PAGE,
  ZUMPER_ACCOUNT,
  ZUMPER_RENTALS_ABOUT_PAGE,
} from './constants'

const THIRTY_MINS_IN_SECONDS = 1_800
const FOUR_HOURS_IN_SECONDS = 14_400
const EIGHT_HOURS_IN_SECONDS = 28_800
const TWELVE_HOURS_IN_SECONDS = 43_200
const FOURTEEN_DAYS_IN_SECONDS = 1_209_600

const paUrlPattern = /[a-z\d-']+-(usa|can)$/

export const patterns = {
  addressId: paUrlPattern,
  bedsPath: /(studios|\d[-]beds)/,
  buildingId: /[apb]?\d+/,
  cityName: /\w[-\w.']*/,
  cityState: /\w[-\w.']*-[a-z]{2}/,
  extra: /[/].*/,
  filtersPath: FILTERS_PATH_RE,
  hoodName: /[\w'][-\w.']+/,
  listingId: LISTING_ID_RE,
  // NOTE: locationPath collides with filtersPath, using this pattern as-is will result in hoods like "no-fee"
  locationPath: /[/]\w[-\w.']*-[a-z]{2}(?:[/][\w'][-\w.']+)?/,
  messagesPath: /messages(?:\/[:a-z0-9A-Z-_]+)?$/,
  nearMeType: new RegExp(VALID_NEAR_ME_TYPE_ROUTES.join('|')),
  propertyTypePath: /(?:apartments|condos|houses|rooms)-for-rent/,
  providerId: /.+/,
  seoSlug: /[/].*?/,
  stateCode: /[a-z]{2}/,
}

// city-state only (without hood)
export const cityLocationPath = new RegExp(`[/]${patterns.cityState.source}`)
// city-state with hood
export const hoodLocationPath = new RegExp(
  `[/]${patterns.cityState.source}[/]${patterns.hoodName.source}`
)
// state-only (without city)
export const stateLocationPath = new RegExp(`[/]${patterns.stateCode.source}`)

const LISTING_DETAIL_PATH_REGEXS = [
  // TODO: remove APTS_BASE_URL once detail route is fully migrated
  new RegExp(
    `^/(${LISTING_BASE_URL}|${APTS_BASE_URL})/(?<listingId>${patterns.listingId.source})(?<filtersPath>${FILTERS_PATH_RE.source})?/?$`
  ),
  new RegExp(
    `^/(${LISTING_BASE_URL}|${APTS_BASE_URL})/(?<listingId>${patterns.listingId.source})(?<seoSlug>${patterns.seoSlug.source})?(?<filtersPath>${FILTERS_PATH_RE.source})?/?$`
  ),
]

const BUILDING_DETAIL_PATH_REGEXS = [
  new RegExp(
    `^/${BLDG_BASE_URL}/(?<buildingId>${patterns.buildingId.source})(?<filtersPath>${FILTERS_PATH_RE.source})?/?$`
  ),
  new RegExp(
    `^/${BLDG_BASE_URL}/(?<buildingId>${patterns.buildingId.source})(?<seoSlug>${patterns.seoSlug.source})?(?<filtersPath>${FILTERS_PATH_RE.source})?/?$`
  ),
]

const XFR_PATH_REGEXS = [
  new RegExp(
    `^/(?<propertyTypePath>${patterns.propertyTypePath.source})(?<locationPath>${cityLocationPath.source})?(?<filtersPath>${FILTERS_PATH_RE.source})?/?$`
  ),
  new RegExp(
    `^/(?<propertyTypePath>${patterns.propertyTypePath.source})(?<locationPath>${hoodLocationPath.source})?(?<filtersPath>${FILTERS_PATH_RE.source})?/?$`
  ),
  new RegExp(
    `^/(?<propertyTypePath>${patterns.propertyTypePath.source})(?<locationPath>${stateLocationPath.source})?(?<filtersPath>${FILTERS_PATH_RE.source})?/?$`
  ),
]

const RENT_RESEARCH_PATH_REGEXS = [
  new RegExp(
    `^/rent-research/(?<cityState>${patterns.cityState.source})(?:\\/(?<bedsPath>${patterns.bedsPath.source}))?/?$`
  ),
  new RegExp(
    `^/rent-research/(?<cityState>${patterns.cityState.source})/(?<hoodName>${patterns.hoodName.source})(?:\\/(?<bedsPath>${patterns.bedsPath.source}))?/?$`
  ),
]

/**
 * compile a route from the path and options. specifying some additional rules
 * - replace all `.` with `[.]` meaning `foo.jpg` matches the dot exactly. use of dot as a
 * match-all is only allowed within a variable
 * - strict defaults to `false` unless explicitly `true`
 * - exact defaults to `true` unless explicitly `false`
 * - patterns uses the module default `patterns` unless another set is provided in options.
 *
 * @param path
 * @param options
 */
export const compileRoute = (
  path: string,
  options: Omit<ZumperRoute, 'path'> = {}
): ZumperRoute => {
  return {
    path,
    strict: false,
    exact: true,
    ...options,
  }
}

export const homepageRoutes = [
  compileRoute('/', {
    name: IS_ZUMPER_RENTALS ? HOMEPAGE_ZUMPER_RENTALS : HOMEPAGE,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
    surrogateControl: {
      public: true,
      maxAge: THIRTY_MINS_IN_SECONDS,
      noTransform: true,
    },
  }),
]

export const strPageRoutes = [
  compileRoute('/str', {
    name: STR_LANDING,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
  }),
]

export const strProviderInterstitialRoutes = [
  compileRoute('/provider-interstitial', {
    name: STR_PROVIDER_INTERSTITIAL,
    cacheControl: { noCache: true, noTransform: true },
  }),
]

export const nearMeRoutes = [
  compileRoute('/near-me', {
    name: NEAR_ME,
    cacheControl: { private: true, maxAge: 300, noTransform: true },
  }),
  compileRoute('/near-me/:nearMeType', {
    name: NEAR_ME,
    cacheControl: { private: true, maxAge: 300, noTransform: true },
  }),
] as const

export const listingDetailRoutes = [
  // apartments-for-rent paths are now legacy and will be removed
  `/apartments-for-rent/:listingId(${patterns.listingId.source})`,
  `/apartments-for-rent/:listingId(${patterns.listingId.source})/*`,
  `/${LISTING_BASE_URL}/:listingId`,
  `/${LISTING_BASE_URL}/:listingId/*`,
].map((path) =>
  compileRoute(path, {
    name: FULL_DETAIL_VIEW,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
    surrogateControl: {
      public: true,
      maxAge: EIGHT_HOURS_IN_SECONDS,
      staleWhileRevalidate: FOUR_HOURS_IN_SECONDS,
      staleIfError: FOURTEEN_DAYS_IN_SECONDS,
      noTransform: true,
    },
    handle: {
      getLegacyParams: (pathname: string) =>
        getLegacyReactRouterParams(pathname, LISTING_DETAIL_PATH_REGEXS),
    },
  })
)

export const buildingDetailRoutes = [
  `/${BLDG_BASE_URL}/:buildingId`,
  `/${BLDG_BASE_URL}/:buildingId/*`,
].map((path) =>
  compileRoute(path, {
    name: FULL_DETAIL_VIEW,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
    surrogateControl: {
      public: true,
      maxAge: EIGHT_HOURS_IN_SECONDS,
      staleWhileRevalidate: FOUR_HOURS_IN_SECONDS,
      staleIfError: FOURTEEN_DAYS_IN_SECONDS,
      noTransform: true,
    },
    handle: {
      getLegacyParams: (pathname: string) =>
        getLegacyReactRouterParams(pathname, BUILDING_DETAIL_PATH_REGEXS),
    },
  })
)

export const detailRoutes = [...listingDetailRoutes, ...buildingDetailRoutes]

export const permanentAddressRoutes = [
  compileRoute(`/${ADDR_BASE_URL}/:addressId`, {
    name: PERMANENT_ADDRESS,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
    surrogateControl: {
      public: true,
      maxAge: EIGHT_HOURS_IN_SECONDS,
      staleWhileRevalidate: FOUR_HOURS_IN_SECONDS,
      staleIfError: FOURTEEN_DAYS_IN_SECONDS,
      noTransform: true,
    },
  }),
]

export const currentSearchRoutes = [
  '/apartments-for-rent/',
  '/condos-for-rent/',
  '/houses-for-rent/',
  '/rooms-for-rent/',
].map(
  (path): ZumperRoute => ({
    path,
    strict: false,
    exact: false,
    name: CURRENT_SEARCH_ROUTE,
    surrogateControl: (context): CacheControl => {
      if (context.isBot) {
        return {
          public: true,
          maxAge: TWELVE_HOURS_IN_SECONDS,
          staleWhileRevalidate: FOUR_HOURS_IN_SECONDS,
          staleIfError: TWELVE_HOURS_IN_SECONDS,
          noTransform: true,
        }
      }
      return {
        public: true,
        maxAge: THIRTY_MINS_IN_SECONDS,
        staleIfError: TWELVE_HOURS_IN_SECONDS,
        noTransform: true,
      }
    },
    handle: {
      getLegacyParams: (pathname: string) => getLegacyReactRouterParams(pathname, XFR_PATH_REGEXS),
    },
  })
)

export const maplistRoutes = [
  compileRoute('/favorites', { name: FAVORITES_ROUTE }),
  compileRoute('/messaged', { name: MESSAGED_ROUTE }),
  compileRoute('/alerts', { name: SAVED_SEARCHES_SUMMARY_ROUTE }),
  // alertId has options \d+|all|new
  compileRoute('/alerts/:alertId', { name: SAVED_SEARCHES_ROUTE }),
  ...currentSearchRoutes,
]

export const forYouRoutes = [
  compileRoute('/for-you', {
    name: FOR_YOU,
    cacheControl: { noCache: true },
  }),
]

export const aboutPageRoutes = [
  compileRoute('/about', {
    name: IS_ZUMPER_RENTALS ? ZUMPER_RENTALS_ABOUT_PAGE : ABOUT_LANDING,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
  }),
]

export const newsroomPageRoutes = [
  compileRoute('/newsroom', {
    name: NEWSROOM_PAGE,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
  }),
]

const RENT_RESEARCH_SURROGATE_CONTROL = {
  public: true,
  maxAge: EIGHT_HOURS_IN_SECONDS,
  staleWhileRevalidate: FOUR_HOURS_IN_SECONDS,
  staleIfError: FOURTEEN_DAYS_IN_SECONDS,
  noTransform: true,
}

export const rentResearchRoutes = [
  compileRoute('/rent-research', {
    name: RENT_RESEARCH_HOMEPAGE,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
    surrogateControl: RENT_RESEARCH_SURROGATE_CONTROL,
  }),
  compileRoute('/rent-research/*', {
    name: RENT_RESEARCH_DETAIL,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
    surrogateControl: RENT_RESEARCH_SURROGATE_CONTROL,
    handle: {
      getLegacyParams: (pathname: string) =>
        getLegacyReactRouterParams(pathname, RENT_RESEARCH_PATH_REGEXS),
    },
  }),
]

// web-zumper doesn't make any authenticated API calls server-side (unlike web-pro, it isn't set up to do so -- we'd need to add things like passing along the sessionid cookie from the incoming request along to the API requests we're making)
// https://zumper.atlassian.net/browse/ZUM-65555 clarifies that the API calls we'll be making on this page should only be attempted client-side
// So all that will be SSRed is the header and a loading indicator, which is safe to cache and share between users
export const messagesRoutes = [
  compileRoute(`${MESSAGING_BASE_PATH}/:streamChannelId?`, {
    name: MESSAGES_PAGE,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
    surrogateControl: RENT_RESEARCH_SURROGATE_CONTROL,
  }),
]

export const notificationSettingsRoutes = [
  compileRoute('/notifications', {
    name: NOTIFICATION_SETTINGS,
    cacheControl: { private: true, noStore: true },
  }),
]

export const multifamilyLandingRoutes = [
  compileRoute('/multifamily/virtualtour', {
    name: VIRTUAL_TOURS_PAGE,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
  }),
]

export const transunionTOSRoutes = [
  compileRoute('/transunion-tos', {
    name: TRANSUNION_TOS,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
  }),
]

export const ccpaRoutes = [
  compileRoute('/privacy/request', {
    name: CCPA_REQUEST,
    cacheControl: { noCache: true, noTransform: true },
  }),
  compileRoute('/ccpa/request', {
    name: CCPA_REQUEST,
    cacheControl: { noCache: true, noTransform: true },
  }),
  compileRoute('/privacy/request-confirmation', {
    name: CCPA_CONFIRMATION,
    cacheControl: { noCache: true, noTransform: true },
  }),
  compileRoute('/ccpa/request-confirmation', {
    name: CCPA_CONFIRMATION,
    cacheControl: { noCache: true, noTransform: true },
  }),
]

export const fairHousingRoutes = [
  compileRoute('/fair-housing-rights', {
    name: FAIR_HOUSING,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
  }),
]

export const privacyPolicyRoutes = [
  compileRoute('/privacy-policy', {
    name: PRIVACY_POLICY,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
  }),
  compileRoute('/privacy-policy/10-2022', {
    name: PRIVACY_POLICY_10_2022,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
  }),
]

export const termsAndConditionsRoutes = [
  compileRoute('/terms-and-conditions', {
    name: TERMS_AND_CONDITIONS,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
  }),
]

export const communityListingsTermsRoutes = [
  compileRoute('/community-listings-terms-of-service', {
    name: COMMUNITY_LISTINGS_TERMS,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
  }),
  compileRoute('/community-listings-terms-of-service/exhibit-a', {
    name: SIGNATURE_VIRTUAL_TERMS,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
  }),
  compileRoute('/community-listings-terms-of-service/exhibit-b', {
    name: SNAPPR_TERMS,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
  }),
  compileRoute('/community-listings-terms-of-service/exhibit-c', {
    name: GOOGLE_POSTING_SERVICES_TERMS,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
  }),
  compileRoute('/community-listings-terms-of-service/exhibit-d', {
    name: REVIEWS_TERMS,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
  }),
  compileRoute('/community-listings-terms-of-service/exhibit-e', {
    name: NURTURE_BOSS_TERMS,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
  }),
]

export const affordabilityCalculatorRoutes = [
  compileRoute('/rent-calculator', {
    name: AFFORDABILITY_CALCULATOR,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
  }),
]

export const aiAssistantRoutes = [
  compileRoute('/assistant', {
    name: AI_ASSISTANT,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
  }),
]

export const careersPageRoutes = [
  compileRoute('/careers', {
    name: CAREERS_PAGE,
    cacheControl: { public: true, maxAge: 300, noTransform: true },
  }),
]

export const sitemapRoutes = [
  compileRoute('/sitemap', {
    name: SITEMAP_ENTRY_POINT,
    cacheControl: { public: true, maxAge: 86400, noTransform: true },
    surrogateControl: { public: true, maxAge: 86400, noTransform: true },
  }),
  compileRoute('/sitemap/:type/:page', {
    name: SITEMAP_CHILD_PAGE,
    cacheControl: { public: true, maxAge: 86400, noTransform: true },
    surrogateControl: { public: true, maxAge: 86400, noTransform: true },
  }),
]

export const zumperAccountRoutes = [
  compileRoute('/zumper-account', {
    exact: false,
    name: ZUMPER_ACCOUNT,
    cacheControl: { noCache: true, noTransform: true },
  }),
  compileRoute('/zumper-account/signin', {
    name: SIGN_IN,
    cacheControl: { noCache: true, noTransform: true },
  }),
  compileRoute('/zumper-account/signup', {
    name: SIGN_UP,
    cacheControl: { noCache: true, noTransform: true },
  }),
]

// NOTE: Here we have defined all of the routes without their components. This
// is useful for when you need to programmatically match a route elsewhere in the
// app. If the components were defined here, then importing this file would
// inadvertently import all of the route components.
//
// We zip the route definitions with the route components in routes/index.js
export const routes = [
  // homepage
  ...homepageRoutes,

  ...nearMeRoutes,

  // detail page
  ...detailRoutes,

  // for you routes
  ...forYouRoutes,

  // maplist / rail routes
  ...maplistRoutes,

  // notification settings page
  ...notificationSettingsRoutes,

  // newsroom page
  ...newsroomPageRoutes,

  // multifamily landing page
  ...multifamilyLandingRoutes,

  // permanent address
  ...permanentAddressRoutes,

  // rent research page
  ...rentResearchRoutes,

  // about page
  ...aboutPageRoutes,

  // short term rentals page
  ...strPageRoutes,

  // short term provider interstitial
  ...strProviderInterstitialRoutes,

  // transunion TOS page
  ...transunionTOSRoutes,

  // ccpa routes
  ...ccpaRoutes,

  // fair housing routes
  ...fairHousingRoutes,

  // privacy routes
  ...privacyPolicyRoutes,

  // termsAndConditions routes
  ...termsAndConditionsRoutes,

  // communityListingsTerms routes
  ...communityListingsTermsRoutes,

  // affordability calculator routes
  ...affordabilityCalculatorRoutes,

  // careers page
  ...careersPageRoutes,

  // AI Assistant routes
  ...aiAssistantRoutes,

  // Sitemap routes
  ...sitemapRoutes,

  // messages routes
  ...messagesRoutes,

  // zumper-account routes
  ...zumperAccountRoutes,
]
